import * as React from 'react'
import {Box, Button, Alert} from '@mui/material'
import {useSnackbar} from 'notistack'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import {sectionCardSx, primaryBtnSx, secondaryBtnSx} from '../../../../styles/patterns'
import {FONT, SIZE} from '../../../../styles/foundation/typography'
import {GAP} from '../../../../styles/foundation/spacing'

import {MERCHANT} from '../data/noberoFreeze'
import {FREEZE_LEVEL_LABEL} from '../data/reference'
import {buildEvidenceIndex} from '../state/dwsEvidence'
import SopDialog from '../SopDialog'
import {toRow, rankRows, filterRows, pickedRows} from './rows'
import {buildRequest} from './request'
import IniFilterBar from './IniFilterBar'
import IniCandidateTable from './IniCandidateTable'
import IniRaiseDrawer from './IniRaiseDrawer'
import IniNewSkuDrawer from './IniNewSkuDrawer'

const alertSx = {fontFamily: FONT, fontSize: SIZE.xs, py: 0.85, '& .MuiAlert-message': {lineHeight: 1.6}}

/**
 * Weight Freeze (v2) — Initiate: Section 4 of 6, the ranked candidate table
 * and the 12-section request drawer.
 *
 * Rows come off the shared store's own `skus` (never the frozen `SKUS`
 * import) so a Catalogue edit or a Packing cascade made earlier in the same
 * session is reflected here. Evidence comes from
 * `state/dwsEvidence.js`'s `buildEvidenceIndex(rows, uploads)` — the ONE
 * rollup for the module — built once and handed to the filter, the
 * stats, the table AND the drawer, rather than recomputed at each of those
 * points the way the source mockup did.
 *
 * Two drawers, one submit path. The table raises from a row's own "Freeze SKU"
 * button, which opens the request drawer over a selection of one — that row,
 * replacing whatever was ticked; the always-present "Freeze weight for a new
 * product" button raises over a product the merchant declares from scratch,
 * which is the only route open to a merchant whose shipment history — and
 * therefore whose candidate table — is empty. Both land in `handleSubmit`, so
 * `buildRequest`, the snackbar and the success alert never learn which drawer
 * called them.
 *
 * Sort and pagination are NOT owned here: `IniCandidateTable` keeps that
 * state locally (see its own header comment), matching
 * `catalogue/CatalogueTable.js`'s precedent. This page hands the table the
 * FILTERED-but-unsorted rows plus the full unfiltered rank list
 * (`allRows`), which the table needs only to resolve the selection contract.
 */
export default function InitiatePage({skus, packs, subcats, requests, uploads, actions, onNavigate, focusFallbackRef, live = false}) {
  const {enqueueSnackbar} = useSnackbar()

  const [q, setQ] = React.useState('')
  const [filter, setFilter] = React.useState('all')
  const [selection, setSelection] = React.useState(() => new Set())
  const [drawerOpen, setDrawerOpen] = React.useState(false)
  // Its OWN boolean. Reusing `drawerOpen` would open both drawers whenever rows
  // happen to be selected.
  const [newSkuOpen, setNewSkuOpen] = React.useState(false)
  const [sopOpen, setSopOpen] = React.useState(false)
  const [raised, setRaised] = React.useState(null)

  const all = React.useMemo(() => rankRows(skus.map((s) => toRow(s))), [skus])
  // Built ONCE — the mockup called its evidence helper inside a
  // filter predicate, a sort accessor AND a cell renderer with no memoisation.
  const evIndex = React.useMemo(() => buildEvidenceIndex(all, uploads), [all, uploads])
  const filtered = React.useMemo(() => filterRows(all, {q, filter}, evIndex), [all, q, filter, evIndex])
  const picked = React.useMemo(() => pickedRows(all, selection), [all, selection])

  const toggleRow = (key) => setSelection((prev) => {
    const next = new Set(prev)
    if (next.has(key)) next.delete(key); else next.add(key)
    return next
  })
  const selectPage = (keys, select) => setSelection((prev) => {
    const next = new Set(prev)
    keys.forEach((k) => { if (select) next.add(k); else next.delete(k) })
    return next
  })
  const clearSelection = () => setSelection(new Set())

  // What a row's own freeze overwrote, held only for as long as its drawer is
  // open. A ref rather than state because nothing renders from it.
  //
  // THE INVARIANT: non-null exactly while a row-freeze drawer is open.
  // `freezeOne` is the only path that opens that drawer and it always writes
  // the ref first; both ways out clear it again — `closeDrawer` once it has
  // restored, `handleSubmit` because a raised request has consumed the rows it
  // replaced. Written as an explicit `!== null` rather than a truthiness test
  // only for the reading: the value is often an EMPTY Set (the merchant had
  // nothing ticked), which is still a real answer to restore, and `!== null`
  // says so without the reader having to recall that `new Set()` is truthy.
  // The two tests behave identically; the guard is legibility, not behaviour.
  const displaced = React.useRef(null)

  // One row, one request. REPLACES the selection instead of going through
  // `toggleRow`: on an already-ticked row a toggle would UNtick it and the
  // drawer's own `picked.length > 0` guard would then have nothing to mount,
  // and on any other row it would raise over everything else still ticked.
  // Both writes are in one handler so React batches them — the drawer must
  // never mount for a render in which `picked` is still the old selection,
  // because its sub-category seed (see IniRaiseDrawer) runs once, at mount.
  //
  // The overwrite is remembered because the rows it discards are the
  // merchant's own work, ticked across filters and pages. Without the undo in
  // `closeDrawer`, one exploratory click would drop a twelve-row selection
  // with no warning, AND leave its own row ticked afterwards — a SKU the
  // merchant explicitly backed out of, still counted to them in the strip
  // above the grid as though they had chosen it.
  const freezeOne = (key) => {
    displaced.current = selection
    setSelection(new Set([key]))
    setDrawerOpen(true)
  }
  const clearFilters = () => { setQ(''); setFilter('all') }

  // Same defensive backstop the rest of the module's drawers use (see
  // DwsPage's closeUploadDrawer/closeRecordDrawer): only steals focus back
  // to the rail when the browser has genuinely nowhere else to put it.
  const restoreFocus = React.useCallback(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    window.requestAnimationFrame(() => {
      if (document.activeElement === document.body && focusFallbackRef && focusFallbackRef.current) {
        focusFallbackRef.current.focusActive()
      }
    })
  }, [focusFallbackRef])

  // Cancel, never submit — `handleSubmit` closes the drawer itself and does its
  // own tidy-up. So this is exactly the moment to undo the one-row overwrite
  // `freezeOne` made to get here, the empty Set included: nothing ticked before
  // the click is the selection to put back.
  const closeDrawer = React.useCallback(() => {
    setDrawerOpen(false)
    if (displaced.current !== null) {
      setSelection(displaced.current)
      displaced.current = null
    }
    restoreFocus()
  }, [restoreFocus])

  const closeNewSkuDrawer = React.useCallback(() => {
    setNewSkuOpen(false)
    restoreFocus()
  }, [restoreFocus])

  // No backstop on the ordinary close (Escape, the X, the backdrop): the button
  // that opened the dialog is still on screen behind it, so MUI's own focus
  // restore lands there and nothing is dropped.
  const closeSop = React.useCallback(() => setSopOpen(false), [])

  // A step link inside the SoP is the case that does need one. It closes the
  // dialog AND navigates, and every destination but step 4's unmounts this page
  // — taking the trigger with it — so MUI restores focus onto a detached node
  // and the browser falls back to <body>. Same rAF backstop the drawers use.
  //
  // Closing first is deliberate: the dialog must not be left over a screen it no
  // longer describes if a destination ever renders in place rather than
  // replacing this page.
  const navigateFromSop = React.useCallback((id) => {
    setSopOpen(false)
    onNavigate(id)
    restoreFocus()
  }, [onNavigate, restoreFocus])

  // Everything a raise does AFTER the request exists, shared by both modes: close whichever drawer
  // was open, consume the selection the row-freeze path owns, announce it, recover focus.
  function afterRaised(request) {
    // One submit path for both drawers (see ./request's buildRequest contract):
    // closing the one that is not open is a no-op.
    setDrawerOpen(false)
    setNewSkuOpen(false)
    // Only the row-freeze path owns the selection: it was raised over rows this
    // page put there itself, so the request consumes them, and the ref
    // remembering what they displaced goes with them (see THE INVARIANT above).
    // The new-product drawer never touched the selection and must not throw the
    // merchant's ticked rows away on its way out.
    if (drawerOpen) {
      setSelection(new Set())
      displaced.current = null
    }
    setRaised({id: request.id, subject: request.subject, level: request.level})
    enqueueSnackbar(
      `Request ${request.id} raised — ${FREEZE_LEVEL_LABEL[request.level]} level, ${request.subject}. Awaiting Prozo review.`,
      {variant: 'success'},
    )
    // A backstop, not a certainty. Both openers — a row's own "Freeze SKU" and
    // the page-level "Freeze weight for a new product" — are still on screen
    // after a submit, so MUI usually restores focus to whichever was pressed.
    // What it cannot restore from is the drawer UNMOUNTING under it: each is
    // rendered conditionally here, and clearing the state that mounted it takes
    // the focused node out of the document in the same commit. The rAF inside
    // only acts when focus really did land on <body>.
    restoreFocus()
  }

  function handleSubmit(payload) {
    if (live) {
      // The backend mints the reference and applies its rules (the category rule among them);
      // the drawer stays open on a refusal so the merchant can fix and resend.
      const pack = (packs || []).find((p) => p.id === payload.packId) || null
      actions.raiseRequest(payload, {packLabel: pack ? pack.label : null, packSelfWeightG: pack && pack.selfWeightG != null ? pack.selfWeightG : null})
        .then((request) => afterRaised(request))
        .catch((err) => enqueueSnackbar((err && err.message) || 'Could not raise the freeze request', {variant: 'error'}))
      return
    }
    const request = buildRequest(payload, {id: MERCHANT.id, name: MERCHANT.name}, requests)
    actions.addRequest(request)
    afterRaised(request)
  }

  const freeze = (MERCHANT.type && MERCHANT.type.freeze) || 'none'
  const noFreeze = freeze === 'none'

  return (
    <Box sx={{display: 'grid', gap: GAP.lg}}>
      {/* Dataset-conditional: Nobero is a 'prozo'-initiated account, so this
          never renders for the shipped dataset. Kept for any account where
          `type.freeze === 'none'` — not dead code.

          The page's heading, its subtitle, the packing-material policy alert and
          the four source-data tiles were all removed on request: the tab that
          mounts this page is itself labelled "Initiate weight freeze", and the
          five request-status cards now sit directly above the tab row, so a
          second stat row here read as a duplicate of the strip rather than as
          different information. */}
      {noFreeze && (
        <Alert severity='info' sx={alertSx}>
          No freeze runs on this account yet — every shipment currently bills on the courier&apos;s own
          reading. Raising a request below starts the first one.
        </Alert>
      )}

      {raised && (
        <Alert
          severity='success' sx={alertSx}
          action={<Button size='small' sx={secondaryBtnSx} startIcon={<AssignmentOutlinedIcon sx={{fontSize: 15}} />} onClick={() => onNavigate('requests')}>View in requests and statuses</Button>}
        >
          Request {raised.id} raised — {FREEZE_LEVEL_LABEL[raised.level]} level, {raised.subject}. Awaiting Prozo review.
        </Alert>
      )}

      {/* The page's two actions. They are OUTSIDE `sectionCardSx`, so they never
          sit in the same box as the table's own per-row "Freeze SKU" — those
          appear inside the grid, one per candidate row, and only for a merchant
          who has shipped; these are always here, because a merchant with no
          shipment history has no other route to a freeze. */}
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: GAP.sm, flexWrap: 'wrap'}}>
        {/* The SoP lost its sidebar row when Weight Freeze became one child of
            Weight Management, so the procedure has to be offered from the one
            screen that acts on it — this is the moment a merchant wants to
            check what Prozo will ask for. It reads first and sits at the far
            edge opposite the action it prepares you for: reading is the
            alternative to raising a freeze, not a variant of it, and pairing
            the two at the same end invited a mis-click between them.
            Secondary treatment for the same reason.

            It opens over this page rather than replacing it: the merchant is
            mid-task here — a filter typed, a place found in a long ranked
            table — and checking what Prozo asks for is a lookup, not a
            departure. Leaving and coming back would have cost that work and
            made the reader find their row again. */}
        <Button
          size='small'
          sx={secondaryBtnSx}
          startIcon={<InfoOutlinedIcon sx={{fontSize: 15}} />}
          onClick={() => setSopOpen(true)}
        >
          Weight freeze SOP
        </Button>
        <Button
          size='small'
          variant='contained'
          sx={primaryBtnSx}
          startIcon={<AcUnitOutlinedIcon sx={{fontSize: 15}} />}
          onClick={() => setNewSkuOpen(true)}
        >
          Freeze weight for a new product
        </Button>
      </Box>

      {live ? (
        // T19 staged release: raising a request is live, the candidate table is not — it ranks
        // the frozen Nobero catalogue, and a real request raised over a sample SKU would be a
        // request about nothing. Until the catalogue connects, the one live route is the
        // new-product drawer above, which asks for everything the backend needs.
        <Alert severity='info' sx={alertSx}>
          <Box component='span' sx={{fontWeight: 700}}>Requests you raise here go to Prozo.</Box>{' '}
          The ranked SKU table is not connected to your catalogue yet, so it is hidden; use{' '}
          <Box component='span' sx={{fontWeight: 700}}>Freeze weight for a new product</Box> to describe the SKU, its weight, its packing and
          your evidence. Category-level requests must name a category exactly as it appears in your product master.
        </Alert>
      ) : (
        <Box sx={{...sectionCardSx, display: 'grid', gap: GAP.sm}}>
          <IniFilterBar
            q={q}
            filter={filter}
            onQChange={setQ}
            onFilterChange={setFilter}
            resultCount={filtered.length}
            placeholder='Search SKU or sub-category'
          />
          <IniCandidateTable
            rows={filtered}
            allRows={all}
            packs={packs}
            selection={selection}
            onToggle={toggleRow}
            onSelectPage={selectPage}
            onClear={clearSelection}
            onFreezeOne={freezeOne}
            onClearFilters={clearFilters}
            // The table's empty state opens the same drawer the header button
            // does — it is the only route out for a merchant with no history.
            onFreezeNewSku={() => setNewSkuOpen(true)}
          />
        </Box>
      )}

      {drawerOpen && picked.length > 0 && (
        <IniRaiseDrawer
          open={drawerOpen}
          packs={packs}
          rows={picked}
          evIndex={evIndex}
          onClose={closeDrawer}
          onSubmit={handleSubmit}
        />
      )}

      {/* Mounted only while open — see IniNewSkuDrawer's header. Toggling `open`
          on a permanent mount would carry a cancelled draft, and its blob urls,
          into the next visit. */}
      {newSkuOpen && (
        <IniNewSkuDrawer
          open={newSkuOpen}
          packs={packs}
          skus={skus}
          // The catalogue's own sub-category map. This drawer's level chooser
          // has no ticked rows to read sub-categories off, so the options come
          // from the catalogue instead — see its own header.
          subcats={subcats}
          onClose={closeNewSkuDrawer}
          onSubmit={handleSubmit}
        />
      )}

      {/* Mounted only while open, like the two drawers above. The SoP's seven
          accordions are collapsed on a fresh read, and a merchant reopening the
          procedure is starting a fresh read — carrying the last visit's open
          panels back would be state nobody asked to keep. It also keeps
          SopPage's counters off this page's render path while it is shut.
          `subcats` is threaded from the store for those counters and, since the
          new-product drawer gained a level chooser, for its sub-category
          options too — those two, and nothing else on this page. */}
      {sopOpen && (
        <SopDialog
          open={sopOpen}
          onClose={closeSop}
          onNavigate={navigateFromSop}
          skus={skus}
          requests={requests}
          subcats={subcats}
          packs={packs}
        />
      )}
    </Box>
  )
}
